import React, { useEffect, useState } from "react";
import { api, EXPENSE_CATEGORIES, fmtEuro, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Loader2, Repeat, Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const catLabel = (k) => EXPENSE_CATEGORIES.find((c) => c.key === k)?.label || k;

const EMPTY = {
  label: "",
  category: "assurance",
  amount: 0,
  day_of_month: 5,
  active: true,
  start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
  end_date: "",
  notes: "",
};

export default function ChargesFixes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recurring-expenses");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      label: r.label,
      category: r.category,
      amount: r.amount,
      day_of_month: r.day_of_month,
      active: !!r.active,
      start_date: r.start_date,
      end_date: r.end_date || "",
      notes: r.notes || "",
    });
    setEditingId(r.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount) || 0,
        day_of_month: Math.max(1, Math.min(28, Number(form.day_of_month) || 1)),
        end_date: form.end_date || null,
      };
      const { data } = editingId
        ? await api.put(`/recurring-expenses/${editingId}`, payload)
        : await api.post("/recurring-expenses", payload);
      toast.success(
        `${editingId ? "Charge mise à jour" : "Charge ajoutée"}${data.generated ? ` — ${data.generated} dépense(s) générée(s)` : ""}`
      );
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, cascade = false) => {
    const msg = cascade
      ? "Supprimer cette charge ET toutes les dépenses générées ?"
      : "Supprimer cette charge ? (les dépenses générées seront conservées)";
    if (!window.confirm(msg)) return;
    await api.delete(`/recurring-expenses/${id}?cascade=${cascade}`);
    toast.success("Supprimée");
    load();
  };

  const toggle = async (r) => {
    await api.put(`/recurring-expenses/${r.id}`, { ...r, active: !r.active });
    toast.success(r.active ? "Charge désactivée" : "Charge réactivée");
    load();
  };

  const triggerGenerate = async () => {
    const { data } = await api.post("/recurring-expenses/generate");
    toast.success(data.generated > 0 ? `${data.generated} dépense(s) générée(s)` : "Aucune nouvelle dépense à générer");
    load();
  };

  const totalMensuel = items.filter((r) => r.active).reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Automatisation</div>
          <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Charges fixes récurrentes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Générées automatiquement chaque mois — plus besoin de les ressaisir.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={triggerGenerate} data-testid="btn-generate-now">
            <RefreshCw className="h-4 w-4 mr-2" /> Générer maintenant
          </Button>
          <Button onClick={openCreate} data-testid="btn-add-recurring">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle charge
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat label="Charges actives" value={items.filter((r) => r.active).length} icon={Repeat} />
        <Stat label="Total mensuel" value={fmtEuro(totalMensuel)} icon={Calendar} accent="primary" />
        <Stat label="Total annuel projeté" value={fmtEuro(totalMensuel * 12)} icon={Calendar} accent="destructive" />
      </div>

      {/* List */}
      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="border-dashed border-2 border-border/50 m-4 rounded-xl p-10 text-center">
            <Repeat className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-manrope font-semibold text-lg mb-1">Aucune charge fixe</div>
            <div className="text-sm text-muted-foreground mb-6">Ajoutez assurance, crédit, téléphone, internet… en une fois.</div>
            <Button onClick={openCreate} data-testid="btn-add-recurring-empty"><Plus className="h-4 w-4 mr-2" /> Nouvelle charge</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-4 py-3">Libellé</th>
                <th className="text-left px-4 py-3">Catégorie</th>
                <th className="text-right px-4 py-3">Montant/mois</th>
                <th className="text-center px-4 py-3">Jour</th>
                <th className="text-left px-4 py-3">Depuis</th>
                <th className="text-center px-4 py-3">Actif</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody data-testid="recurring-tbody">
              {items.map((r) => (
                <tr key={r.id} className={`border-b border-border/60 hover:bg-muted/40 ${!r.active ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3 font-medium">{r.label}</td>
                  <td className="px-4 py-3">{catLabel(r.category)}</td>
                  <td className="px-4 py-3 text-right num font-semibold">{fmtEuro(r.amount)}</td>
                  <td className="px-4 py-3 text-center num text-muted-foreground">{r.day_of_month}</td>
                  <td className="px-4 py-3 num text-muted-foreground">{r.start_date}</td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`switch-active-${r.id}`} />
                  </td>
                  <td className="px-2 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-foreground p-1.5" data-testid={`btn-edit-recurring-${r.id}`}>
                        Modifier
                      </button>
                      <button onClick={() => remove(r.id, false)} className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-destructive/10" data-testid={`btn-delete-recurring-${r.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div className="card-surface p-4 text-xs text-muted-foreground leading-relaxed">
        <p>
          <strong className="text-foreground">Fonctionnement :</strong> chaque charge active génère
          automatiquement une dépense datée au jour indiqué, du mois de démarrage jusqu&apos;au mois courant.
          La génération est déclenchée lors du chargement du tableau de bord et de la liste des dépenses.
          Vous pouvez la forcer avec le bouton <em>Générer maintenant</em>.
        </p>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-manrope">{editingId ? "Modifier la charge" : "Nouvelle charge fixe"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Libellé</Label>
              <Input required placeholder="Ex : Assurance VTC + RC Pro" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="input-recurring-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="select-recurring-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Montant mensuel (€)</Label>
                <Input type="number" step="0.01" required inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="input-recurring-amount" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Jour du prélèvement (1-28)</Label>
                <Input type="number" min="1" max="28" required value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} data-testid="input-recurring-day" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Actif</Label>
                <div className="h-10 flex items-center">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="switch-recurring-active" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Date de début</Label>
                <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="input-recurring-start" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Fin (optionnel)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="input-recurring-end" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="input-recurring-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} data-testid="btn-save-recurring">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent = "muted" }) {
  const tones = {
    muted: "border-border bg-card",
    primary: "border-primary/25 bg-primary/5",
    destructive: "border-destructive/25 bg-destructive/5",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[accent]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="overline">{label}</span>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="font-manrope font-extrabold text-2xl num">{value}</div>
    </div>
  );
}
